import React, { useId, useState } from 'react';
import { cx } from './hooks';

/**
 * items: [{ key, label, icon, children, disabled }]
 * Panels render the first time they are opened and then stay mounted (hidden),
 * so form fields inside other tabs keep their state.
 */
const Tabs = ({ items = [], activeKey, defaultActiveKey, onChange, className, navClassName }) => {
  const baseId = useId();
  const [innerKey, setInnerKey] = useState(defaultActiveKey ?? items[0]?.key);
  const current = activeKey ?? innerKey;
  const [visited, setVisited] = useState(() => new Set([current]));

  if (!visited.has(current)) setVisited(new Set(visited).add(current));

  const select = key => {
    if (activeKey === undefined) setInnerKey(key);
    onChange?.(key);
  };

  return (
    <div className={cx('tabs', className)}>
      <div className={cx('tabs-nav', navClassName)} role="tablist">
        {items.map(item => (
          <button
            key={item.key}
            type="button"
            role="tab"
            id={`${baseId}-tab-${item.key}`}
            aria-selected={item.key === current}
            aria-controls={`${baseId}-panel-${item.key}`}
            className={cx('tab', item.key === current && 'is-active')}
            disabled={item.disabled}
            onClick={() => select(item.key)}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
      {items.map(item =>
        visited.has(item.key) ? (
          <div
            key={item.key}
            role="tabpanel"
            id={`${baseId}-panel-${item.key}`}
            aria-labelledby={`${baseId}-tab-${item.key}`}
            hidden={item.key !== current}
          >
            {item.children}
          </div>
        ) : null
      )}
    </div>
  );
};

export default Tabs;
